import queryString from 'query-string';
import { ENDPOINTS } from 'config/endpoints';
import { useQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { SequenceInstance } from 'crono-fe-common/types/sequenceInstance';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';

/**
 * The endpoint answers 200 with a null body when the instance cannot be
 * resolved for the acting user — a stopped or reassigned sequence, or one
 * owned by somebody other than the contact's owner — hence the nullable data.
 */
export default function useGetSequenceInstance(
  sequenceInstanceId: number,
  prospectId: string,
  withLogs: boolean,
  getRefetchInterval?: (
    data: Response<SequenceInstance | null> | undefined,
  ) => number | false,
  //Opens a sequence owned by another user, acting as its owner. Manager only
  onBehalf?: boolean,
) {
  const { isManager } = useRoleCapabilities();
  //The flag is omitted rather than sent as false: non-managers sending it at all are rejected with 403
  const actOnBehalf = !!onBehalf && isManager;

  const queryParams = queryString.stringify({
    sequenceInstanceId,
    prospectId,
    withLogs,
    ...(actOnBehalf && { onBehalf: true }),
  });

  const request: Request = {
    url: `${ENDPOINTS.strategy.prospect}?${queryParams}`,
    config: {
      method: 'get',
    },
  };

  return useQuery<Response<SequenceInstance | null>, CronoError>({
    //Every value the request varies by, so two callers asking differently
    //cannot read each other's cached answer
    queryKey: [
      ENDPOINTS.strategy.prospect,
      sequenceInstanceId,
      prospectId,
      withLogs,
      actOnBehalf,
    ],
    queryFn: () => client(request),
    enabled: sequenceInstanceId > 0,
    refetchInterval: (data, query) => {
      if (getRefetchInterval) {
        return getRefetchInterval(data);
      }
      return false;
    },
  });
}
